import React, { useRef } from 'react';
import { Camera, Loader2, ImageOff, ArrowUp, ArrowDown, Star } from 'lucide-react';
import { CardControls } from './AdminControls';
import { api } from '../lib/api';
import LinkifiedText from './LinkifiedText';

/**
 * Shared "What's Happening" news card used on the Home page and News & Links page.
 * - Renders the news item's photo above the text when `image_url` is set.
 * - When admin is logged in, exposes inline photo upload / remove controls that
 *   mirror the church-card pattern.
 * - Optional Move Up / Move Down arrows when the parent wires up reordering.
 */
const NewsCard = ({
  news,
  isAdmin = false,
  uploading = false,
  onEdit,
  onDelete,
  onFileChosen,
  onRemoveImage,
  onMoveUp,
  onMoveDown,
  onToggleFeature,
  canMoveUp = false,
  canMoveDown = false,
}) => {
  const inputRef = useRef(null);
  const src = api.newsImageAbs(news.image_url);

  const handlePick = () => inputRef.current?.click();
  const handleChange = (e) => {
    const f = e.target.files?.[0];
    if (f && onFileChosen) onFileChosen(news, f);
    if (inputRef.current) inputRef.current.value = '';
  };

  return (
    <article
      className="relative group bg-white border border-[#e9e0cc] hover:border-[#3a2359] hover:shadow-sm overflow-hidden flex flex-col"
      data-testid={`news-card-${news.id}`}
    >
      {src ? (
        <div className="relative w-full aspect-[16/10] overflow-hidden bg-[#f2ead6]">
          <img
            src={src}
            alt={news.title}
            className="w-full h-full object-cover group-hover:scale-[1.03]"
            style={{ transition: 'transform 700ms ease' }}
          />
          {isAdmin && (
            <div className="absolute top-2 left-2 flex gap-2">
              <button
                type="button"
                onClick={handlePick}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/95 hover:bg-white text-[#3a2359] text-[11px] font-medium shadow disabled:opacity-70"
                data-testid={`news-change-photo-${news.id}`}
              >
                {uploading ? <><Loader2 size={12} className="animate-spin" /> Uploading…</> : <><Camera size={12} /> Change</>}
              </button>
              <button
                type="button"
                onClick={() => onRemoveImage && onRemoveImage(news)}
                disabled={uploading}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/95 hover:bg-white text-[#8a2b1a] text-[11px] font-medium shadow disabled:opacity-70"
                data-testid={`news-remove-photo-${news.id}`}
              >
                <ImageOff size={12} /> Remove
              </button>
            </div>
          )}
        </div>
      ) : (
        isAdmin && (
          <div className="w-full aspect-[16/10] bg-[#f7f0dd] border-b border-[#e9e0cc] flex items-center justify-center">
            <button
              type="button"
              onClick={handlePick}
              disabled={uploading}
              className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-[#e9e0cc] hover:border-[#3a2359] text-[#3a2359] text-xs font-medium disabled:opacity-70"
              data-testid={`news-add-photo-${news.id}`}
            >
              {uploading ? <><Loader2 size={14} className="animate-spin" /> Uploading…</> : <><Camera size={14} /> Add a photo</>}
            </button>
          </div>
        )
      )}

      <div className="p-6 flex-1 flex flex-col">
        {isAdmin && (onEdit || onDelete) && (
          <CardControls onEdit={onEdit} onDelete={onDelete} />
        )}
        <p className="text-xs tracking-[0.2em] uppercase text-[#a68a5b] font-medium mb-2">{news.date}</p>
        <h3 className="font-display text-xl text-[#2a1a45] mb-2 leading-tight">{news.title}</h3>
        <LinkifiedText as="p" text={news.excerpt} className="text-sm text-[#5a534a] leading-relaxed whitespace-pre-wrap" />
        {isAdmin && (onMoveUp || onMoveDown || onToggleFeature) && (
          <div className="mt-4 pt-3 border-t border-[#f2ead6] flex items-center justify-between gap-2">
            {onToggleFeature ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onToggleFeature(news); }}
                title={news.featured_on_home ? 'Remove from home page' : 'Feature on home page'}
                aria-label={news.featured_on_home ? 'Remove from home page' : 'Feature on home page'}
                data-testid={`news-toggle-feature-${news.id}`}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 border text-[10px] tracking-[0.15em] uppercase font-medium ${
                  news.featured_on_home
                    ? 'bg-[#3a2359] border-[#3a2359] text-[#e6d4a8] hover:bg-[#4d2f75]'
                    : 'bg-white border-[#3a2359] text-[#3a2359] hover:bg-[#3a2359] hover:text-white'
                }`}
              >
                <Star size={12} fill={news.featured_on_home ? 'currentColor' : 'none'} />
                {news.featured_on_home ? 'Featured' : 'Feature on Home'}
              </button>
            ) : (
              <span className="text-[10px] tracking-[0.2em] uppercase text-[#a68a5b] font-medium">Reorder</span>
            )}
            {(onMoveUp || onMoveDown) && (
              <div className="flex items-center gap-1.5">
                {onToggleFeature && (
                  <span className="text-[10px] tracking-[0.2em] uppercase text-[#a68a5b] font-medium">Reorder</span>
                )}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onMoveUp && onMoveUp(news); }}
                  disabled={!canMoveUp}
                  data-testid={`news-move-up-${news.id}`}
                  aria-label="Move up"
                  className="inline-flex items-center justify-center w-8 h-8 border border-[#3a2359] text-[#3a2359] bg-white hover:bg-[#3a2359] hover:text-white disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-[#3a2359] disabled:cursor-not-allowed"
                >
                  <ArrowUp size={14} />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onMoveDown && onMoveDown(news); }}
                  disabled={!canMoveDown}
                  data-testid={`news-move-down-${news.id}`}
                  aria-label="Move down"
                  className="inline-flex items-center justify-center w-8 h-8 border border-[#3a2359] text-[#3a2359] bg-white hover:bg-[#3a2359] hover:text-white disabled:opacity-30 disabled:hover:bg-white disabled:hover:text-[#3a2359] disabled:cursor-not-allowed"
                >
                  <ArrowDown size={14} />
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {isAdmin && (
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          className="hidden"
          onChange={handleChange}
          data-testid={`news-file-input-${news.id}`}
        />
      )}
    </article>
  );
};

export default NewsCard;
